
import { Emitter } from "../../../base/common/events.js";
import { Disposable, IDisposable } from "../../../base/common/lifecycle.js";
import { ILogService } from "../../log/common/log.js";
import { IResourceData, IResourceViewProvider, IResourceService, ResourceChangedArguments, IResourceView } from "./resources.js";



// #region Resource Service

export class ResourceService extends Disposable implements IResourceService {

    readonly _serviceBrand: undefined;

    private readonly resources: Map<object, WeakRef<IResourceData>> = new Map();
    private readonly resourceIterator = new ResourceIterator(this.resources);
    private readonly metadataProviders: Map<string, IResourceViewProvider> = new Map();

    private readonly _onDidChange = this._register(new Emitter<ResourceChangedArguments>());
    readonly onDidChange = this._onDidChange.event;

    constructor(
        @ILogService private readonly logService: ILogService
    ) {
        super();
    }

    // Use FinalizationRegistry to detect when resources are garbage collected
    private readonly registry = new FinalizationRegistry((key: object) => {
        this.logService.trace(`Resource has been garbage collected.`);
        // Remove from resources map
        this.resources.delete(key);
        // Dispose associated view if any
        this.viewsCache.onDispose(key as IResourceData);
        // Fire change event
        this._onDidChange.fire({ resourceDisposed: true });
    });

    registerResource(resource: IResourceData): void {
        this.resources.set(resource.reference, new WeakRef(resource));
        this.registry.register(resource, resource.reference);
        this._onDidChange.fire({ resourceAdded: true });
    }

    iterateResources(): Iterable<IResourceData> {
        return this.resourceIterator;
    }



    registerProvider(scheme: string, provider: IResourceViewProvider): IDisposable {
        this.metadataProviders.set(scheme, provider);
        return {
            dispose: () => {
                this.metadataProviders.delete(scheme);
            }
        };
    }

    private readonly viewsCache = new ViewCache();

    getResourceView<T extends IResourceData>(scheme: string, resource: T): IResourceView | null {
        // Check cache first
        let view = this.viewsCache.get(scheme, resource);
        if (view) {
            return view;
        }
        // Get provider for scheme
        const provider = this.metadataProviders.get(scheme);
        if (!provider) {
            return null;
        }
        // Get view from provider
        view = provider.getResourceView(resource);
        if (view) {
            this.viewsCache.add(scheme, resource, view);
        }
        return view;
    }
}


class ResourceIterator implements Iterable<IResourceData> {
    constructor(private readonly resources: Map<object, WeakRef<IResourceData>>) { }
    *[Symbol.iterator](): IterableIterator<IResourceData> {
        for (const [key, weakRef] of this.resources.entries()) {
            const resource = weakRef.deref();
            if (resource) {
                yield resource;
            } else {
                this.resources.delete(key); // Clean up dead references
            }
        }
    }
}



/**
 * A simple cache for resource views, keyed by scheme and resource.  
 * Uses WeakMap to allow garbage collection of resources and their views.
 */
class ViewCache {
    private readonly cache = new Map<string, WeakMap<IResourceData, IResourceView>>();


    add(scheme: string, resource: IResourceData, view: IResourceView) {
        let schemeCache = this.cache.get(scheme);
        if (!schemeCache) {
            schemeCache = new WeakMap();
            this.cache.set(scheme, schemeCache);
        }
        schemeCache.set(resource, view);
    }

    get(scheme: string, resource: IResourceData) {
        const schemeCache = this.cache.get(scheme);
        return schemeCache?.get(resource) || null;
    }

    onDispose(resource: IResourceData) {
        for (const schemeCache of this.cache.values()) {
            const view = schemeCache.get(resource);
            if (view) {
                view.dispose();
            }
            schemeCache.delete(resource);
        }
    }
}
